import { TitleH1Component } from "components/TitleH1Component";
import { TitleH3Component } from "components/TitleH3Component";
import ValidaComponent from "components/ValidaComponent";
import { TextsContext } from "context/textsContext";
import { useMyFavoritePlanForm } from "context/MyFavoritePlanFormContext";
import { useContext } from "react";
import { useQuery } from "@apollo/client";
import { GET_COUNTRIES } from "graphql/queries";
import CountriesChildrenOnly from "utils/CountriesChildrenOnly.json";
import { capitalizeEveryWord } from "utils/Generics";
import { deriveBirthdayFromAge } from "helpers/toolsHelper";
import { transformData } from "components/utils/RequestQuoter.util";
import { quoter } from "api/feature/quoterApi";
import styleForm from "scss/form.module.scss";
import Stylegeneral from "scss/general.module.scss";

const BaseInformationStep = () => {
    const { texts, getCurrentLanguage } = useContext(TextsContext);
    const { favoritePlanFormState, handleOnChange, handleChecks, addCalculation, addCalculationError } = useMyFavoritePlanForm();

    const { data } = useQuery(GET_COUNTRIES, {
        variables: { field: 'zone', language: getCurrentLanguage(), skip: 0, take: 300 }
    });

    const getCountries = () => {
        const countries = data?.countries?.filter(country => !CountriesChildrenOnly.includes(country.isoNum));
        return countries?.sort((a, b) => a?.description.localeCompare(b?.description));
    }

    // Mirrors the eligibility rule from MyFavoritePlanFormContext#isFemale:
    // maternity only applies to female applicants aged 18-43.
    const isFemaleEligibleForMaternity = (gender, age) => {
        const numericAge = Number(age);
        // eslint-disable-next-line eqeqeq
        return gender == 2 && numericAge >= 18 && numericAge <= 43;
    };

    // Mirrors the quoter's own Step1Pages customHandleOnChange: gender/age/country
    // all feed pricing, so recalculate immediately on change instead of waiting
    // for the user to reach a benefit-selection step. Builds a locally-patched
    // context rather than reading favoritePlanFormState directly, since the
    // reducer update from handleOnChange() above hasn't committed/re-rendered yet.
    const customHandleOnChange = (e) => {
        handleOnChange(e);

        try {
            const { name, value } = e.target;
            const newContext = { ...favoritePlanFormState, [name]: value };

            if (name === 'age') {
                newContext.birthday = deriveBirthdayFromAge(value);
            }

            // Turning gender to male, or moving age outside the 18-43 window while
            // female, makes the maternity benefit ineligible: switch it off so the
            // stale selection doesn't linger and get priced/saved.
            if ((name === 'gender' || name === 'age') && newContext.outpationBenefitsmaternidad
                && !isFemaleEligibleForMaternity(newContext.gender, newContext.age)) {
                newContext.outpationBenefitsmaternidad = false;
                handleOnChange({ target: { name: 'outpationBenefitsmaternidad', value: false } });
            }

            const reqQuoter = transformData(newContext, getCurrentLanguage(), null);

            quoter(reqQuoter)
                .then(response => {
                    if (response && response !== undefined && response?.data !== null) {
                        addCalculation(response);
                    } else {
                        addCalculationError();
                    }
                });

        } catch (error) {
            console.error('Error fetching calculated value:', error);
            addCalculationError();
        }
    }

    return (
        <div>
            <TitleH1Component styleGroup={`raleway_semibold fw_700  color_primary mb-2`}>
                {texts?.favorite_plan_form_base_info_title ?? 'Base Information'}
            </TitleH1Component>
            <TitleH3Component styleGroup={`raleway fw_400 color_primary pb-3 mb-0`}>
                {texts?.favorite_plan_form_base_info_subtitle ?? 'Fill out the information below to start building a personalized plan'}
            </TitleH3Component>

            <div className={`${Stylegeneral.rowForm} row gy-5 mt-2 mx-0`}>
                <div className={`col-12 col-md-4 ${Stylegeneral.colform}`}>
                    <div className={`${styleForm.form_group}`}>
                        <label htmlFor="gender" className={`${styleForm.labels} raleway color_primary_2`}>
                            <span className={styleForm.punto}>*</span>
                            {texts?.general_form_field_gender ?? 'Gender'}
                        </label>
                        <select className={`${styleForm.customselectLine}`} name={"gender"} value={favoritePlanFormState.gender} onChange={customHandleOnChange}>
                            <option value={0} disabled={true}>{texts?.general_form_field_select ?? 'Select'}</option>
                            <option value={1}>{texts?.general_form_option_male ?? 'Male'}</option>
                            <option value={2}>{texts?.general_form_option_female ?? 'Female'}</option>
                        </select>
                        <ValidaComponent keyName={"gender"} />
                    </div>
                </div>

                <div className={`col-12 col-md-4 ${Stylegeneral.colform}`}>
                    <div className={`${styleForm.form_group}`}>
                        <label htmlFor="age" className={`${styleForm.labels} raleway color_primary_2`}>
                            <span className={styleForm.punto}>*</span>
                            {texts?.favorite_plan_form_field_age ?? 'Age'}
                        </label>
                        <input
                            type="number"
                            className={`${styleForm.input_with_line}`}
                            placeholder={texts?.favorite_plan_form_field_age ?? 'Age'}
                            name={"age"}
                            value={favoritePlanFormState.age}
                            onChange={customHandleOnChange}
                        />
                        <ValidaComponent keyName={"age"} />
                    </div>
                </div>

                <div className={`col-12 col-md-4 ${Stylegeneral.colform}`}>
                    <div className={`${styleForm.form_group}`}>
                        <label htmlFor="country" className={`${styleForm.labels} raleway color_primary_2`}>
                            <span className={styleForm.punto}>*</span>
                            {texts?.general_form_field_country ?? 'Country of residence'}
                        </label>
                        <select className={`${styleForm.customselectLine}`} name={"country"} value={favoritePlanFormState.country} onChange={customHandleOnChange}>
                            <option value={0} disabled={true}>{texts?.general_form_field_select ?? 'Select'}</option>
                            {
                                getCountries()
                                    ?.map(country => (
                                        <option key={country?.id} value={country?.isoNum}>{capitalizeEveryWord(country?.description)}</option>
                                    ))
                            }
                        </select>
                        <ValidaComponent keyName={"country"} />
                    </div>
                </div>

                <div className="col-12 col-md-4">
                    <div className={`${styleForm.btn_activar}`}>
                        <label htmlFor="addDependant" className={`${styleForm.labels} ${styleForm.sin_tootip} raleway mb-2 color_primary_2 fw_700 ms-2 ms-md-0`}>
                            {texts?.favorite_plan_form_field_needs_dependent ?? 'Do you need to add a dependent?'}
                        </label>
                        <div className={`form-check form-switch input-personalizado ${styleForm.col_input}`}>
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="addDependant"
                                name={"addDependant"}
                                onChange={handleChecks}
                                checked={favoritePlanFormState.addDependant}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
};

export default BaseInformationStep;
